import { Fragment, useEffect, useMemo, useState } from "react";
import { CoreComposer } from "./CoreComposer";
import { CoreIcon } from "./CoreIcon";
import { ThreadActivityPanel } from "./ThreadActivityPanel";
import {
  initialActivityState,
  type ActivityConnection,
  type ActivityStreamState,
} from "../../shared/activityStream";
import type {
  CoreComposerProps,
  MaybePromise,
  ThreadCitation,
  ThreadClarification,
  ThreadFinding,
  ThreadOutput,
} from "./types";
import "./threadCitations.css";

type ContextTab = "activity" | "outputs" | "sources";

/**
 * Anchor fields a citation may carry so that activating a marker resolves to the
 * exact passage of the exact document version, not merely to the document.
 *
 * Every field is optional on purpose. Callers that only have the document-level
 * data keep type-checking, and the UI then says plainly that the version anchor
 * was not recorded rather than implying a precision the stored data does not have.
 */
export interface ThreadCitationAnchor {
  documentId?: string;
  documentVersionId?: string;
  versionNumber?: number;
  versionLabel?: string;
  chunkId?: string;
  page?: number | string | null;
  pageStart?: number;
  pageEnd?: number;
  paragraphIndex?: number;
  passage?: string;
  verificationStatus?: string;
}

export type ThreadCitationDetail = ThreadCitation & ThreadCitationAnchor;

export type AnswerSegment =
  | { kind: "text"; text: string }
  | { kind: "marker"; marker: number };

const MARKER_PATTERN = /\[(\d{1,3})\]/g;

/**
 * Split answer text into plain runs and inline [n] citation markers so that the
 * markers can be rendered as controls instead of dead text.
 */
export function parseAnswerSegments(paragraph: string): AnswerSegment[] {
  const source = typeof paragraph === "string" ? paragraph : "";
  const segments: AnswerSegment[] = [];
  let cursor = 0;
  MARKER_PATTERN.lastIndex = 0;
  let match = MARKER_PATTERN.exec(source);
  while (match) {
    if (match.index > cursor) segments.push({ kind: "text", text: source.slice(cursor, match.index) });
    segments.push({ kind: "marker", marker: Number(match[1]) });
    cursor = match.index + match[0].length;
    match = MARKER_PATTERN.exec(source);
  }
  if (cursor < source.length) segments.push({ kind: "text", text: source.slice(cursor) });
  return segments;
}

export interface CitationTargetSummary {
  documentName: string;
  versionText: string;
  versionId: string;
  chunkId: string;
  locatorText: string;
  passage: string;
  checkText: string;
  hasVersionAnchor: boolean;
  hasPassageAnchor: boolean;
  precisionNote: string;
}

const VERIFICATION_TEXT: Record<string, string> = {
  verified: "Quote located in the cited version",
  extractive: "Quote taken from the source text, not re-located",
  failed: "Quote could not be located in the source",
};

/**
 * Describe exactly what a citation can be traced to, using only what was stored.
 * Nothing here infers a location; a missing anchor is reported as missing.
 */
export function describeCitationTarget(citation: ThreadCitationDetail): CitationTargetSummary {
  const documentName = citation.title || "Source document";
  const versionId = citation.documentVersionId || "";
  const hasVersionAnchor = Boolean(versionId);
  const versionText = citation.versionLabel
    || (typeof citation.versionNumber === "number" ? `Version ${citation.versionNumber}` : "")
    || (hasVersionAnchor ? "Cited version" : "");
  const passage = (citation.passage || citation.excerpt || "").trim();
  const hasPassageAnchor = Boolean(passage);

  const locatorParts: string[] = [];
  if (citation.pageStart && citation.pageEnd && citation.pageEnd !== citation.pageStart) {
    locatorParts.push(`Pages ${citation.pageStart}–${citation.pageEnd}`);
  } else if (citation.page !== undefined && citation.page !== null && citation.page !== "") {
    locatorParts.push(`Page ${citation.page}`);
  } else if (citation.pageStart) {
    locatorParts.push(`Page ${citation.pageStart}`);
  }
  if (typeof citation.paragraphIndex === "number") {
    locatorParts.push(`paragraph index ${citation.paragraphIndex}`);
  }
  if (!locatorParts.length && citation.locator) locatorParts.push(citation.locator);
  const locatorText = locatorParts.join(" · ") || "Location not recorded";

  const notes: string[] = [];
  if (!hasVersionAnchor) {
    notes.push("No document version was stored with this citation, so it can only be traced to the document.");
  }
  if (!hasPassageAnchor) {
    notes.push("No passage text was stored with this citation.");
  }

  return {
    documentName,
    versionText,
    versionId,
    chunkId: citation.chunkId || "",
    locatorText,
    passage,
    checkText: citation.verificationStatus ? VERIFICATION_TEXT[citation.verificationStatus] || citation.verificationStatus : "",
    hasVersionAnchor,
    hasPassageAnchor,
    precisionNote: notes.join(" "),
  };
}

function citationTargetLabel(marker: number, summary: CitationTargetSummary): string {
  const parts = [`Source ${marker}`, summary.documentName];
  if (summary.versionText) parts.push(summary.versionText);
  parts.push(summary.locatorText);
  return parts.join(" · ");
}

export interface ThreadScreenProps {
  author: string;
  time: string;
  prompt: string;
  attachments?: Array<{ id: string; label: string; kind?: "outline" | "neutral" }>;
  clarification?: ThreadClarification | null;
  statusLabel?: string;
  answerParagraphs?: string[];
  citationMarkersByParagraph?: number[][];
  findings?: ThreadFinding[];
  citations?: ThreadCitationDetail[];
  outputs?: ThreadOutput[];
  notificationSet?: boolean;
  isRunning?: boolean;
  /**
   * The live narration of this run, folded from the thread's activity stream. A finished
   * thread is seeded from what the server stored, so the panel has one input shape and
   * the screen never carries two differing accounts of the same run.
   */
  activity?: ActivityStreamState;
  activityConnection?: ActivityConnection;
  /** Whether the user has asked to see the model's raw working-out. Default off. */
  showWorking?: boolean;
  onShowWorkingChange?: (next: boolean) => void;
  showNudge?: boolean;
  nudgeColumns?: string[];
  followUpComposer?: CoreComposerProps;
  onAnswerClarification?: (answerId: string | null) => MaybePromise;
  onCitationOpen?: (citation: ThreadCitationDetail) => MaybePromise;
  onNotificationChange?: (enabled: boolean) => MaybePromise;
  onOpenOutput?: (output: ThreadOutput) => void;
  onDownloadOutput?: (output: ThreadOutput) => MaybePromise;
  onSaveOutput?: (output: ThreadOutput) => MaybePromise;
  onBuildReviewTable?: () => MaybePromise;
  onEditReviewColumns?: () => void;
  onExit?: () => void;
  onDelete?: () => MaybePromise;
}

export function ThreadScreen({
  author,
  time,
  prompt,
  attachments = [],
  clarification,
  statusLabel = "",
  answerParagraphs = [],
  citationMarkersByParagraph = [],
  findings = [],
  citations = [],
  outputs = [],
  notificationSet = false,
  isRunning = false,
  activity,
  activityConnection = "idle",
  showWorking = false,
  onShowWorkingChange,
  showNudge = true,
  nudgeColumns = ["Counterparty", "Change of control", "Assignment", "MFN", "Consent required"],
  followUpComposer,
  onAnswerClarification,
  onCitationOpen,
  onNotificationChange,
  onOpenOutput,
  onDownloadOutput,
  onSaveOutput,
  onBuildReviewTable,
  onEditReviewColumns,
  onExit,
  onDelete,
}: ThreadScreenProps) {
  const [clarificationAnswer, setClarificationAnswer] = useState<string | null | undefined>(clarification?.answerId);
  const [contextTab, setContextTab] = useState<ContextTab>("activity");
  const [activeCitationId, setActiveCitationId] = useState("");
  const [localNotificationSet, setLocalNotificationSet] = useState(notificationSet);
  const [busy, setBusy] = useState("");

  useEffect(() => setClarificationAnswer(clarification?.answerId), [clarification?.answerId]);
  useEffect(() => setLocalNotificationSet(notificationSet), [notificationSet]);

  const citationMap = useMemo(
    () => new Map(citations.map((citation) => [citation.marker, citation])),
    [citations]
  );

  // There is no plan-approval state left to hold. A question submits and the work starts;
  // the panel below narrates it. Threads created before that change may still be parked in
  // the retired awaiting-approval state on the server, and the deliberate behaviour for one
  // of those is that it simply runs and displays — this screen offers no control that would
  // do nothing, and no branch here can produce one.
  const activityState = activity || initialActivityState();

  const answerClarification = async (answerId: string | null) => {
    if (!onAnswerClarification) return;
    setBusy("clarification");
    try {
      await onAnswerClarification(answerId);
      setClarificationAnswer(answerId);
    } finally {
      setBusy("");
    }
  };

  const openCitation = async (citation: ThreadCitationDetail) => {
    setActiveCitationId(citation.id);
    setContextTab("sources");
    // The whole citation travels, anchors included, so the host resolves to the
    // cited version and passage rather than merely opening the document.
    await onCitationOpen?.(citation);
  };

  const renderCitationMarker = (marker: number, key: string) => {
    const citation = citationMap.get(marker);
    if (!citation) return <Fragment key={key}>[{marker}]</Fragment>;
    const summary = describeCitationTarget(citation);
    return (
      <button
        key={key}
        type="button"
        className="di-core-citation di-core-citation--inline"
        aria-label={citationTargetLabel(marker, summary)}
        title={citationTargetLabel(marker, summary)}
        data-citation-marker={marker}
        data-citation-id={citation.id}
        data-citation-document-id={citation.documentId || ""}
        data-citation-version-id={citation.documentVersionId || ""}
        data-citation-chunk-id={citation.chunkId || ""}
        onClick={() => void openCitation(citation)}
      >
        [{marker}]
      </button>
    );
  };

  const toggleNotification = async () => {
    if (!onNotificationChange) return;
    const next = !localNotificationSet;
    setBusy("notification");
    try {
      await onNotificationChange(next);
      setLocalNotificationSet(next);
    } finally {
      setBusy("");
    }
  };

  const selectedClarification = clarification?.choices.find((choice) => choice.id === clarificationAnswer);

  return (
    <main className="di-core-screen di-core-thread">
      <div className="di-core-thread__conversation">
        <div className="di-core-thread__scroll">
          <article className="di-core-thread__content">
            <header className="di-core-thread__author">
              <span><strong>{author}</strong><time>{time}</time></span>
              <div className="di-core-thread__actions">
                <button type="button" className="di-core-btn di-core-btn--ghost" onClick={onExit}>Exit thread</button>
                <button
                  type="button"
                  className="di-core-btn di-core-btn--ghost"
                  disabled={!onDelete || busy === "delete-thread"}
                  // A control that is on screen and inert says why. Found by the guard in
                  // ThreadScreen.noPlanApproval.test.tsx, which checks every disabled
                  // button on the screen rather than only the ones that were removed.
                  title={!onDelete ? "This thread cannot be deleted from here." : undefined}
                  onClick={() => void (async () => {
                    if (!onDelete || !window.confirm("Delete this thread? This removes it from your active history.")) return;
                    setBusy("delete-thread");
                    try {
                      await onDelete();
                    } finally {
                      setBusy("");
                    }
                  })()}
                >
                  {busy === "delete-thread" ? "Deletingâ€¦" : "Delete"}
                </button>
              </div>
            </header>
            <p className="di-core-thread__prompt">{prompt}</p>
            <div className="di-core-tags">
              {attachments.map((attachment) => (
                <span key={attachment.id} className={`di-core-tag di-core-tag--${attachment.kind || "outline"}`}>{attachment.label}</span>
              ))}
            </div>

            <hr className="di-core-hr" />

            <ThreadActivityPanel
              state={activityState}
              connection={activityConnection}
              showWorking={showWorking}
              onShowWorkingChange={onShowWorkingChange}
            />

            {clarification && clarificationAnswer === undefined && (
              <section className="di-core-clarification">
                <p className="di-core-kicker">DI needs one clarification</p>
                <p>{clarification.question}</p>
                <div className="di-core-actions">
                  {clarification.choices.map((choice) => (
                    <button
                      type="button"
                      key={choice.id}
                      className="di-core-btn di-core-btn--secondary"
                      disabled={!onAnswerClarification || Boolean(busy)}
                      onClick={() => void answerClarification(choice.id)}
                    >
                      {choice.label}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="di-core-btn di-core-btn--ghost"
                    disabled={!onAnswerClarification || Boolean(busy)}
                    onClick={() => void answerClarification(null)}
                  >
                    Skip · use your judgement
                  </button>
                </div>
              </section>
            )}
            {clarification && clarificationAnswer !== undefined && (
              <p className="di-core-clarification-result">
                <CoreIcon name="check" size={14} />
                {selectedClarification ? selectedClarification.label : "Skipped · DI will use its judgement"}
              </p>
            )}

            <hr className="di-core-hr" />

            <section className="di-core-answer" aria-labelledby="di-core-answer-title">
              <div className="di-core-answer__heading">
                <p id="di-core-answer-title" className="di-core-kicker">Document Intelligence</p>
                <span>{statusLabel}</span>
              </div>
              {answerParagraphs.map((paragraph, index) => {
                const segments = parseAnswerSegments(paragraph);
                const inlineMarkers = new Set(
                  segments.filter((segment): segment is { kind: "marker"; marker: number } => segment.kind === "marker")
                    .map((segment) => segment.marker)
                );
                const appended = (citationMarkersByParagraph[index] || [])
                  .filter((marker) => !inlineMarkers.has(marker));
                return (
                  <p key={`${index}-${paragraph.slice(0, 20)}`}>
                    {segments.map((segment, segmentIndex) => (
                      segment.kind === "text"
                        ? <Fragment key={`text-${index}-${segmentIndex}`}>{segment.text}</Fragment>
                        : renderCitationMarker(segment.marker, `inline-${index}-${segmentIndex}`)
                    ))}
                    {appended.length > 0 && " "}
                    {appended.map((marker, markerIndex) => (
                      renderCitationMarker(marker, `appended-${index}-${markerIndex}`)
                    ))}
                  </p>
                );
              })}
              {!!findings.length && (
                <div className="di-core-findings">
                  <table>
                    <thead><tr><th>Counterparty</th><th>Finding</th><th>Source</th></tr></thead>
                    <tbody>
                      {findings.slice(0, 3).map((finding) => (
                        <tr key={finding.id}>
                          <td>{finding.counterparty}</td>
                          <td>{finding.finding}</td>
                          <td>{finding.locator || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {findings.length > 3 && <p>and {findings.length - 3} others</p>}
                </div>
              )}
              {/*
                No second spinner and no second status wording here. The panel above is the
                only place this screen says how the run is going; a status line in two places
                is how the screen came to claim it was complete while it was still working.
              */}
              {isRunning && !answerParagraphs.length && (
                <p className="di-core-running">The answer will appear here once the work above finishes.</p>
              )}
            </section>

            {showNudge && (
              <section className="di-core-nudge">
                <div>
                  <p className="di-core-kicker">Next useful step</p>
                  <h3>Turn these findings into a review table?</h3>
                  <p>{nudgeColumns.join(" · ")}</p>
                  <div className="di-core-actions">
                    <button
                      type="button"
                      className="di-core-btn di-core-btn--primary"
                      disabled={!onBuildReviewTable}
                      title={!onBuildReviewTable ? "Review-table creation is not available." : undefined}
                      onClick={() => void onBuildReviewTable?.()}
                    >
                      Yes, build a review table
                    </button>
                    <button
                      type="button"
                      className="di-core-btn di-core-btn--secondary"
                      disabled={!onEditReviewColumns}
                      title={!onEditReviewColumns ? "Column editing is not available." : undefined}
                      onClick={onEditReviewColumns}
                    >
                      Edit columns
                    </button>
                  </div>
                </div>
                <span>One table per thread</span>
              </section>
            )}
          </article>
        </div>
        {followUpComposer && (
          <div className="di-core-thread__follow-up">
            {/*
              One mode only. There is no pending plan to revise, so the box is never
              repurposed and is never blocked waiting for an approval that cannot be given.
            */}
            <CoreComposer
              {...followUpComposer}
              key="thread-follow-up"
              compact
              busy={Boolean(followUpComposer.busy)}
              submitLabel={followUpComposer.submitLabel || "Send"}
            />
          </div>
        )}
      </div>

      <aside className="di-core-context" aria-label="Thread context">
        <div className="di-core-tabs" role="tablist" aria-label="Thread context views">
          {(["activity", "outputs", "sources"] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={contextTab === tab}
              onClick={() => setContextTab(tab)}
            >
              {tab[0].toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
        <div className="di-core-context__body">
          {contextTab === "activity" && (
            <>
              {/*
                Deliberately NOT a second step list. The live account of this run is in the
                request itself; repeating it here in different words is precisely how the two
                halves of the screen came to disagree. This tab holds the controls only.
              */}
              <p className="di-core-muted">
                What this request is doing, step by step, is shown in the request itself.
              </p>
              <button
                type="button"
                className={`di-core-btn di-core-btn--block ${localNotificationSet ? "di-core-btn--selected" : "di-core-btn--secondary"}`}
                disabled={!onNotificationChange || busy === "notification"}
                title={!onNotificationChange ? "Email completion notifications are not configured." : undefined}
                onClick={() => void toggleNotification()}
              >
                {localNotificationSet ? <><CoreIcon name="check" /> Notification set · email on completion</> : "Notify me when this finishes"}
              </button>
              <p className="di-core-muted">Email notification only. In-app and desktop notifications are not supported.</p>
            </>
          )}
          {contextTab === "outputs" && (
            <div className="di-core-output-list">
              {outputs.map((output) => (
                <article key={output.id}>
                  <p className="di-core-kicker">{output.kind}</p>
                  <h3>{output.name}</h3>
                  <p>{output.meta}</p>
                  <div className="di-core-output-list__links">
                    {onOpenOutput && <button type="button" onClick={() => onOpenOutput(output)}>Open in editor</button>}
                    {onDownloadOutput && <button type="button" onClick={() => void onDownloadOutput(output)}>Download</button>}
                    {onSaveOutput && <button type="button" onClick={() => void onSaveOutput(output)}>Save to Vault</button>}
                  </div>
                </article>
              ))}
              {!outputs.length && <p className="di-core-empty-copy">Outputs will appear here as each task completes.</p>}
            </div>
          )}
          {contextTab === "sources" && (
            <ol className="di-core-source-list">
              {citations.map((citation) => {
                const summary = describeCitationTarget(citation);
                const isActive = activeCitationId === citation.id;
                return (
                  <li key={citation.id} className={isActive ? "is-active" : ""}>
                    <button
                      type="button"
                      aria-expanded={isActive}
                      aria-label={citationTargetLabel(citation.marker, summary)}
                      onClick={() => void openCitation(citation)}
                    >
                      <span>{citation.marker}</span>
                      <strong>{citation.title}</strong>
                      <small>{citation.type} · {summary.locatorText}</small>
                      {citation.excerpt && <em>{citation.excerpt}</em>}
                    </button>
                    {isActive && (
                      <div
                        className="di-core-citation-detail"
                        data-testid={`citation-detail-${citation.marker}`}
                        data-citation-version-id={summary.versionId}
                        data-citation-chunk-id={summary.chunkId}
                      >
                        <p className="di-core-kicker">Cited passage</p>
                        <blockquote data-testid={`citation-passage-${citation.marker}`}>
                          {summary.hasPassageAnchor ? summary.passage : "No passage text was stored for this citation."}
                        </blockquote>
                        <dl>
                          <dt>Document</dt>
                          <dd>{summary.documentName}</dd>
                          <dt>Version</dt>
                          <dd data-testid={`citation-version-${citation.marker}`}>
                            {summary.hasVersionAnchor ? summary.versionText : "Not recorded"}
                          </dd>
                          {summary.hasVersionAnchor && (
                            <>
                              <dt>Version reference</dt>
                              <dd><code>{summary.versionId}</code></dd>
                            </>
                          )}
                          <dt>Location</dt>
                          <dd>{summary.locatorText}</dd>
                          {summary.chunkId && (
                            <>
                              <dt>Passage reference</dt>
                              <dd><code>{summary.chunkId}</code></dd>
                            </>
                          )}
                          {summary.checkText && (
                            <>
                              <dt>Check</dt>
                              <dd>{summary.checkText}</dd>
                            </>
                          )}
                        </dl>
                        {summary.precisionNote && (
                          <p className="di-core-citation-detail__note" data-testid={`citation-precision-note-${citation.marker}`}>
                            {summary.precisionNote}
                          </p>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
              {!citations.length && <p className="di-core-empty-copy">Cited sources will appear here.</p>}
            </ol>
          )}
        </div>
      </aside>
    </main>
  );
}
